'use client';

import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const CATEGORIES = [
  { value: 'spam', label: 'Спам и реклама' },
  { value: 'harassment', label: 'Оскорбления' },
  { value: 'hate', label: 'Разжигание ненависти' },
  { value: 'other', label: 'Другое' },
] as const;

const REASONS: Record<string, Array<{ value: string; label: string }>> = {
  spam: [
    { value: 'advertising', label: 'Реклама проектов' },
    { value: 'mass_messages', label: 'Массовые сообщения' },
    { value: 'links', label: 'Спам-ссылки' },
  ],
  harassment: [
    { value: 'insults', label: 'Оскорбления' },
    { value: 'threats', label: 'Угрозы' },
  ],
  hate: [{ value: 'hate_speech', label: 'Язык ненависти' }],
  other: [{ value: 'other', label: 'Другая причина' }],
};

export function CommentReportDialog({
  open,
  onOpenChange,
  onSubmit,
  title = 'Пожаловаться на комментарий',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  onSubmit: (
    category: string,
    reason: string,
    details?: string,
  ) => Promise<{ ok: boolean; message?: string }>;
}) {
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setCategory('');
    setReason('');
    setDetails('');
    setConfirmed(false);
  };

  const submit = async () => {
    if (!category || !reason || !confirmed) return;
    setIsSubmitting(true);
    try {
      const result = await onSubmit(category, reason, details.trim() || undefined);
      if (result.ok) {
        toast.success('Жалоба отправлена');
        onOpenChange(false);
        reset();
      } else {
        toast.error(result.message || 'Не удалось отправить жалобу');
      }
    } catch {
      toast.error('Не удалось отправить жалобу. Попробуйте ещё раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen && !isSubmitting) reset();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Выберите категорию и причину жалобы. Администрация рассмотрит вашу жалобу.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Категория</Label>
            <Select
              value={category}
              onValueChange={(value) => {
                setCategory(value);
                setReason('');
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите категорию" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Причина</Label>
            <Select value={reason} onValueChange={setReason} disabled={!category}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите причину" />
              </SelectTrigger>
              <SelectContent>
                {(REASONS[category] ?? []).map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-details">Пояснение (необязательно)</Label>
            <Textarea
              id="report-details"
              value={details}
              onChange={(event) => setDetails(event.target.value.slice(0, 1000))}
              placeholder="Опишите ситуацию"
              rows={4}
            />
            <p className="text-right text-xs text-muted-foreground">{details.length}/1000</p>
          </div>

          <label className="flex cursor-pointer items-start gap-2 text-sm text-[#575E74]">
            <Checkbox
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked === true)}
              className="mt-0.5"
            />
            <span>Подтверждаю, что жалоба обоснована</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={!category || !reason || !confirmed || isSubmitting}>
            {isSubmitting ? 'Отправляем...' : 'Отправить жалобу'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
